import { Button, Card, Flex, Form, FormProps, Input, Table, TableProps, Typography, notification } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import PageTitle from 'components/title/title';
import ComponentHeader from 'components/componentHeader/componentHeader';
import FormPassword from 'components/formPassword/formPassword';
import ListButton from 'components/listButton/listButton';
import StatusComponent from 'components/status/status';
import NoData from 'components/noData/noData';

// models
import { User } from '../models/user';

// sertvices
import { onDeleteUser, onGetUsers, onResetPassword, onSuspendUser, onUnsuspendUser } from './../services/usersService';
import NotificationService from 'core/services/notification';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import { capitalizeWords } from 'utils/capitalizeWords';

type ResetPassFieldsType = {
	password: string;
	password_confirmation: string;
};

const UsersManagementListPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const [resetPasswordForm] = Form.useForm();

	const [users, setUsers] = useState<User[]>([]);
	const [tempUser, setTempUser] = useState<User | null>(null);

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [suspendModalOpen, setSuspendModalOpen] = useState(false);
	const [suspendLoading, setSuspendLoading] = useState<boolean>(false);

	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

	const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
	const [resetPasswordLoading, setResetPasswordLoading] = useState<boolean>(false);

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<User>['columns'] = [
		{
			title: t('USERS_MANAGEMENT.USER_NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name) => capitalizeWords(name),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'account_status',
			key: 'account_status',
			render: (status, row) => (
				<StatusComponent text={isRTL() ? row.translated_account_status.ar : row.translated_account_status.en} color={StatusColors[status]} />
			),
		},
		{
			title: t('COMMON.CREATED_AT'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (date) => dayjs(date).format('DD/MM/YYYY - h:mm a'),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) =>
				record.account_status !== 'Deleted' &&
				record.is_editable && (
					<Flex gap={10} justify="center">
						<ListButton type="edit" text={t('USERS_MANAGEMENT.EDIT_USER')} onClick={() => navigate(`${record.id}`)} />
						<ListButton
							type={record.account_status == 'Active' ? 'suspend' : 'unsuspend'}
							text={record.account_status == 'Active' ? t('USERS_MANAGEMENT.SUSPEND_USER') : t('USERS_MANAGEMENT.UNSUSPEND_USER')}
							onClick={() => {
								setTempUser(record);
								setSuspendModalOpen(true);
							}}
						/>
						<ListButton
							type="reset_password"
							text={t('USERS_MANAGEMENT.RESET_PASSWORD')}
							onClick={() => {
								setTempUser(record);
								setResetPasswordModalOpen(true);
							}}
						/>
						<ListButton
							type="delete"
							text={t('USERS_MANAGEMENT.DELETE_USER')}
							onClick={() => {
								setTempUser(record);
								setDeleteModalOpen(true);
							}}
						/>
					</Flex>
				),
		},
	];

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	useEffect(() => {
		setCurrentPage(getCurrentPage());

		getAllUsersHandler();
	}, []);

	const getAllUsersHandler = async () => {
		try {
			setIsLoading(true);
			const response = await onGetUsers(getCurrentPage());
			setUsers(response.company_users);
			setTotalRecords(response.pagination.total);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetUsers(page);
			setUsers(response.company_users);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const onToggleSuspendUserHandler = async () => {
		try {
			const userId = tempUser?.id as number;
			setSuspendLoading(true);

			if (tempUser?.account_status === 'Active') await onSuspendUser(userId);
			else await onUnsuspendUser(userId);

			setSuspendModalOpen(false);
			setSuspendLoading(false);
			setTempUser(null);
			NotificationService.success(
				t('COMMON.SUCCESS'),
				tempUser?.account_status === 'Active' ? t('USERS_MANAGEMENT.USER_SUSPENDED') : t('USERS_MANAGEMENT.USER_UNSUSPENDED'),
			);
			await getAllUsersHandler();
		} catch (error) {
			setSuspendLoading(false);
			NotificationService.error('COMMON.ERROR', t('COMMON.SUBMIT_ERROR'));
		}
	};

	const onDeleteUserHandler = async () => {
		try {
			const userId = tempUser?.id as number;
			setDeleteLoading(true);
			await onDeleteUser(userId);
			setDeleteModalOpen(false);
			setDeleteLoading(false);
			setTempUser(null);
			NotificationService.success(t('COMMON.SUCCESS'), t('USERS_MANAGEMENT.USER_DELETED'));
			await getAllUsersHandler();
		} catch (error) {
			setDeleteLoading(false);
		}
	};

	const onResetPasswordHandler: FormProps<ResetPassFieldsType>['onFinish'] = async (values) => {
		try {
			const userId = tempUser?.id as number;
			setResetPasswordLoading(true);
			await onResetPassword(userId, values);
			setResetPasswordModalOpen(false);
			setResetPasswordLoading(false);
			resetPasswordForm.resetFields();
			setTempUser(null);
			NotificationService.success(t('COMMON.SUCCESS'), t('USERS_MANAGEMENT.USER_PASSWORD_RESET'));
			await getAllUsersHandler();
		} catch (error) {
			setResetPasswordLoading(true);
		}
	};

	const validatePassword = (_: any, value: string) => {
		if (!value || resetPasswordForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('COMMON.PASSWORDS_NOT_MATCH')));
	};

	return (
		<>
			<PageTitle name={t('TITLE.USERS_MANAGEMENT')} />

			<Card className="mt-4">
				<Flex justify="space-between" align="center">
					<ComponentHeader title={t('USERS_MANAGEMENT.SYSTEM_USERS')} subtitle={t('USERS_MANAGEMENT.TO_MANAGE_YOUR_SYSTEM')} />

					<Button type="primary" size="middle" onClick={() => navigate('create')}>
						{t('USERS_MANAGEMENT.ADD_USER')}
					</Button>
				</Flex>
			</Card>

			<Table
				className="mt-5 bordered"
				columns={columns}
				dataSource={users}
				scroll={{ x: 600 }}
				rowKey="id"
				pagination={{
					pageSize: 8,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
				}}
				locale={{ emptyText: <NoData /> }}
				loading={isLoading}
			/>

			{/* suspend and unsuspend modal */}
			<ConfirmModalComponent
				open={suspendModalOpen}
				confirmText={tempUser?.account_status === 'Active' ? t('USERS_MANAGEMENT.SUSPEND') : t('USERS_MANAGEMENT.UNSUSPEND')}
				isLoading={suspendLoading}
				confirmColor="danger"
				onConfirm={onToggleSuspendUserHandler}
				onDiscard={() => {
					setTempUser(null);
					setSuspendModalOpen(false);
				}}
				title={t('USERS_MANAGEMENT.SUSPEND_USER')}
				subTitle={t('USERS_MANAGEMENT.SUSPEND_USER_DESC')}
			/>

			{/* delete modal */}
			<ConfirmModalComponent
				open={deleteModalOpen}
				confirmText={t('COMMON.DELETE')}
				isLoading={deleteLoading}
				confirmColor="danger"
				onConfirm={onDeleteUserHandler}
				onDiscard={() => {
					setDeleteModalOpen(false);
					setTempUser(null);
				}}
				title={t('USERS_MANAGEMENT.DELETE_USER')}
				subTitle={t('USERS_MANAGEMENT.DELETE_USER_DESC')}
			/>

			{/* reset password modal */}
			<ConfirmModalComponent
				open={resetPasswordModalOpen}
				onConfirm={() => resetPasswordForm.submit()}
				confirmText={t('COMMON.RESET')}
				isLoading={resetPasswordLoading}
				onDiscard={() => {
					resetPasswordForm.resetFields();
					setResetPasswordModalOpen(false);
					setTempUser(null);
				}}
				title={t('USERS_MANAGEMENT.RESET_USER_PASSWORD')}>
				<Form form={resetPasswordForm} onFinish={onResetPasswordHandler}>
					<div>
						<FormPassword
							fullWidth
							name="password"
							label={t('COMMON.PASSWORD')}
							rules={[
								{ required: true, message: t('COMMON.PASSWORD_REQUIRED') },
								{ min: 8, message: t('COMMON.PASSWORD_MIN_8') },
							]}
						/>
					</div>

					<div className="mt-4">
						<FormPassword
							fullWidth
							name="password_confirmation"
							label={t('COMMON.PASSWORD_CONFIRMATION')}
							rules={[{ required: true, message: t('COMMON.PASSWORD_CONFIRMATION_REQUIRED') }, { validator: validatePassword }]}
						/>
					</div>
				</Form>
			</ConfirmModalComponent>
		</>
	);
};

export default UsersManagementListPage;
